import { useState, useEffect, type FC } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";

import { supabase } from "@/shared/lib/supabase";
import { LAWYER_ROUTES, POST_LOSS_ROUTES } from "@/shared/constants/paths";
import { setCachedLawyerStatus } from "@/shared/hooks/useIsLawyer";
import { acceptLawyerInvitation } from "@/shared/utils/invitationAcceptance";
import { getGoogleOAuthContext, clearGoogleOAuthContext } from "@/shared/utils/googleOAuth";
import CircularProgress from "@/shared/components/CircularProgress";

import { AttorneyAuthLayout } from "./components/AttorneyAuthLayout";
import { ProfileSetupStep } from "./components/ProfileSetupStep";
import type { AttorneyProfile } from "./components/ProfileSetupStep";
import { useLawyerSignup } from "./LawyerSignupContext";
import { clearLawyerSignupSource, readLawyerSignupSource } from "./signupSource";

const LawyerProfilePage: FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { clearCredentials } = useLawyerSignup();
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(true);
  const [hasExistingUser, setHasExistingUser] = useState(false);
  const [prefill, setPrefill] = useState<{ firstName?: string; lastName?: string; phone?: string }>(
    {},
  );
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const checkExistingAccount = async () => {
      try {
        const {
          data: { user: authUser },
        } = await supabase.auth.getUser();
        if (!authUser) {
          navigate(LAWYER_ROUTES.SIGNUP, { replace: true });
          return;
        }

        const oauthContext = getGoogleOAuthContext();
        if (oauthContext?.type === "lawyer-signup" || oauthContext?.type === "lawyer-login") {
          if (authUser.email) {
            sessionStorage.setItem("lawyer_signup_email", authUser.email);
          }
          clearGoogleOAuthContext();
        }

        const { data: dbUser } = await supabase
          .from("users")
          .select("user_id, first_name, last_name, phone_number")
          .eq("auth_id", authUser.id)
          .maybeSingle();

        if (dbUser?.first_name) {
          setPrefill({
            firstName: dbUser.first_name,
            lastName: dbUser.last_name || "",
            phone: dbUser.phone_number || "",
          });
        } else {
          const fullName = authUser.user_metadata?.full_name as string | undefined;
          if (fullName) {
            const parts = fullName.trim().split(/\s+/);
            setPrefill({
              firstName: parts[0],
              lastName: parts.slice(1).join(" "),
            });
          }
        }

        if (!dbUser) {
          setChecking(false);
          return;
        }

        const { data: lawyer } = await supabase
          .from("lawyers")
          .select("id, firm_name")
          .eq("user_id", dbUser.user_id)
          .maybeSingle();

        if (!lawyer && dbUser) {
          navigate(POST_LOSS_ROUTES.OVERVIEW, { replace: true });
          return;
        }

        setHasExistingUser(true);

        if (lawyer?.firm_name) {
          const pendingToken = sessionStorage.getItem("lawyer_invite_token");
          if (pendingToken) {
            const accepted = await acceptLawyerInvitation(pendingToken);
            if (!accepted) {
              setChecking(false);
              setError("Failed to link your account to the estate. Please try again.");
              return;
            }
            sessionStorage.removeItem("lawyer_invite_token");
          }
          setCachedLawyerStatus(true);
          clearCredentials();
          sessionStorage.removeItem("lawyer_signup_email");
          navigate(LAWYER_ROUTES.DASHBOARD, { replace: true });
          return;
        }
      } catch {
        // Continue to profile setup
      } finally {
        setChecking(false);
      }
    };

    void checkExistingAccount();
  }, [navigate]);

  const handleSubmit = async (profile: AttorneyProfile) => {
    setLoading(true);
    setError(null);

    try {
      if (!hasExistingUser) {
        const { data: signupData } = await supabase.functions.invoke(
          "account/complete-lawyer-signup",
          {
            body: {
              firstName: profile.firstName,
              lastName: profile.lastName,
              signupSource: readLawyerSignupSource(),
            },
          },
        );

        if (!signupData?.success) {
          setError(
            (signupData?.error as string) || "Failed to complete registration. Please try again.",
          );
          return;
        }
      }

      const { data, error: profileError } = await supabase.functions.invoke(
        "account/update-lawyer-profile",
        { body: profile },
      );

      if (profileError || !data?.success) {
        setError((data?.error as string) || "Failed to save profile. Please try again.");
        return;
      }

      const inviteToken = sessionStorage.getItem("lawyer_invite_token");
      if (inviteToken) {
        const accepted = await acceptLawyerInvitation(inviteToken);
        if (!accepted) {
          setError("Failed to link your account to the estate. Please try again.");
          return;
        }
        sessionStorage.removeItem("lawyer_invite_token");
      }

      clearCredentials();
      sessionStorage.removeItem("lawyer_signup_email");
      clearLawyerSignupSource();
      setCachedLawyerStatus(true);
      await queryClient.refetchQueries({ queryKey: ["auth-user"] });
      queryClient.invalidateQueries({ queryKey: ["auth", "user"] });
      queryClient.invalidateQueries({ queryKey: ["app-user-data"] });
      queryClient.invalidateQueries({ queryKey: ["is-lawyer"] });
      queryClient.invalidateQueries({ queryKey: ["lawyer-settings"] });
      navigate(LAWYER_ROUTES.DASHBOARD, { replace: true });
    } catch {
      setError("An unexpected error occurred");
    } finally {
      setLoading(false);
    }
  };

  if (checking) {
    return (
      <AttorneyAuthLayout>
        <div className="flex items-center justify-center py-20">
          <CircularProgress />
        </div>
      </AttorneyAuthLayout>
    );
  }

  return (
    <AttorneyAuthLayout>
      <ProfileSetupStep
        onSubmit={handleSubmit}
        isLoading={loading}
        error={error}
        prefillFirstName={prefill.firstName}
        prefillLastName={prefill.lastName}
        prefillPhone={prefill.phone}
      />
    </AttorneyAuthLayout>
  );
};

export default LawyerProfilePage;
